import { supabase } from '../lib/supabase'
import { comercialService } from './comercial'
import { comercialGestaoService } from './comercialGestao'
import { dashcorretorService } from './dashcorretor'
import { shopService } from './shop'
import type { HelpdeskQuote } from '../types/comercialCotacoes'
import type { ComercialActivity } from '../types/comercialAtividades'
import type {
  ComercialCockpitBrokerDetail,
  ComercialCockpitCampaignContext,
  ComercialCockpitCampaigns,
  ComercialCockpitData,
  ComercialCockpitDecoleRank,
} from '../types/comercialCockpit'
import type {
  DashCorretorBateuLevouEntry,
  DashCorretorCampeaoMesEntry,
  DashCorretorDecoleInfluenciadoresEntry,
} from '../types/dashcorretor'

const PAGE_SIZE = 1000

const ACTIVITY_SELECT = `
  *,
  broker:brokers(id, name, id_scgp),
  activity_type:helpdesk_commercial_activity_types(id, name),
  visit_objective:helpdesk_commercial_objective_contact(id, name)
`

function normalizeQuote(row: Record<string, unknown>): HelpdeskQuote {
  return {
    id: String(row.id),
    quote_number: Number(row.quote_number),
    version: Number(row.version),
    responsible: (row.responsible as string | null) ?? null,
    company: (row.company as string | null) ?? null,
    state_code: (row.state_code as string | null) ?? null,
    city: (row.city as string | null) ?? null,
    status: (row.status as string | null) ?? null,
    inclusion_at: (row.inclusion_at as string | null) ?? null,
    broker_id: (row.broker_id as string | null) ?? null,
    insured_count: row.insured_count != null ? Number(row.insured_count) : null,
    requester_name: (row.requester_name as string | null) ?? null,
    requester_phone: (row.requester_phone as string | null) ?? null,
    requester_email: (row.requester_email as string | null) ?? null,
    amount: row.amount != null ? Number(row.amount) : null,
    amount_per_life: row.amount_per_life != null ? Number(row.amount_per_life) : null,
    commission: row.commission != null ? Number(row.commission) : null,
    notes: (row.notes as string | null) ?? null,
    updated_at: String(row.updated_at),
    minimum_incoice_amount:
      row.minimum_incoice_amount != null ? Number(row.minimum_incoice_amount) : null,
    total_premium:
      row.total_premium != null
        ? Number(row.total_premium)
        : row.total_premiun != null
          ? Number(row.total_premiun)
          : null,
  }
}

async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const all: T[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const to = from + PAGE_SIZE - 1
    const { data, error } = await buildQuery(from, to)
    if (error) throw error

    const page = data ?? []
    all.push(...page)

    if (page.length < PAGE_SIZE) {
      hasMore = false
    } else {
      from += PAGE_SIZE
    }
  }

  return all
}

async function getBrokerProduction(brokerId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('dashcorretor_values')
    .select('monthly_paid_premium')
    .eq('id_broker', brokerId)
    .maybeSingle()

  if (error) return false
  return (data?.monthly_paid_premium ?? 0) > 0
}

type DecoleTable = 'dashcorretor_campaigns_2026_dv' | 'dashcorretor_campaigns_2026_dp'

async function getDecoleRank(
  table: DecoleTable,
  entries: DashCorretorDecoleInfluenciadoresEntry[]
): Promise<ComercialCockpitDecoleRank | null> {
  const item = entries[0]
  if (!item) return null

  const { count, error } = await supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
    .gt('value', item.value)

  if (error) throw error
  return { item, position: (count ?? 0) + 1 }
}

async function loadCampaignContext(
  brokerId: string,
  campaigns: ComercialCockpitCampaigns
): Promise<ComercialCockpitCampaignContext> {
  const [decoleVendasRank, decoleProducaoRank, brokerPoints, availableCupons] = await Promise.all([
    getDecoleRank('dashcorretor_campaigns_2026_dv', campaigns.decoleVendas),
    getDecoleRank('dashcorretor_campaigns_2026_dp', campaigns.decoleProducao),
    shopService.getBrokerPoints(brokerId).catch(() => null),
    comercialService.listAvailableCuponsByBrokerId(brokerId).catch(() => []),
  ])

  return {
    decoleVendasRank,
    decoleProducaoRank,
    brokerPoints,
    availableCupons,
  }
}

export const comercialCockpitService = {
  async getBrokerById(brokerId: string): Promise<ComercialCockpitBrokerDetail | null> {
    if (!brokerId) return null

    const { data: broker, error } = await supabase
      .from('brokers')
      .select('id, name, id_scgp, classification, classification_previous, state, city')
      .eq('id', brokerId)
      .maybeSingle()

    if (error) throw error
    if (!broker) return null

    const [comercialName, contacts, hasProduction] = await Promise.all([
      comercialGestaoService.getComercialNameByBrokerId(brokerId),
      comercialGestaoService.listBrokerContacts(brokerId),
      getBrokerProduction(brokerId),
    ])

    return {
      id: broker.id,
      name: broker.name ?? '(Sem nome)',
      id_scgp: broker.id_scgp ?? null,
      classification: broker.classification ?? null,
      classification_previous: broker.classification_previous ?? null,
      state: broker.state ?? null,
      city: broker.city ?? null,
      comercial_name: comercialName,
      has_production: hasProduction,
      contacts,
    }
  },

  async getQuotesByBroker(brokerId: string): Promise<HelpdeskQuote[]> {
    if (!brokerId) return []

    try {
      const rows = await fetchAllPages<Record<string, unknown>>((from, to) =>
        supabase
          .from('helpdesk_quotes')
          .select('*')
          .eq('broker_id', brokerId)
          .order('inclusion_at', { ascending: false, nullsFirst: false })
          .order('updated_at', { ascending: false })
          .range(from, to)
          .then(({ data, error }) => ({ data: (data ?? []) as Record<string, unknown>[], error }))
      )

      return rows.map(normalizeQuote)
    } catch {
      const allRows: HelpdeskQuote[] = []
      let offset = 0
      let hasMore = true

      while (hasMore) {
        const { data: batchData, error: batchError } = await supabase.rpc(
          'get_comercial_cotacoes_quotes_search',
          {
            p_limit: PAGE_SIZE,
            p_offset: offset,
          }
        )
        if (batchError) throw batchError

        const batch = ((batchData ?? []) as unknown[]).map((row) => {
          const obj =
            typeof row === 'string'
              ? (JSON.parse(row) as Record<string, unknown>)
              : (row as Record<string, unknown>)
          return normalizeQuote(obj)
        })
        const filtered = batch.filter((q) => q.broker_id === brokerId)
        allRows.push(...filtered)

        if (batch.length < PAGE_SIZE) {
          hasMore = false
        } else {
          offset += PAGE_SIZE
        }
      }

      return allRows.sort((a, b) => {
        const aTime = a.inclusion_at ? new Date(a.inclusion_at).getTime() : -Infinity
        const bTime = b.inclusion_at ? new Date(b.inclusion_at).getTime() : -Infinity
        return bTime - aTime
      })
    }
  },

  async getActivitiesByBroker(brokerId: string): Promise<ComercialActivity[]> {
    if (!brokerId) return []

    return fetchAllPages<ComercialActivity>((from, to) =>
      supabase
        .from('helpdesk_commercial_activities')
        .select(ACTIVITY_SELECT)
        .eq('broker_id', brokerId)
        .is('deleted_at', null)
        .order('activity_date', { ascending: false })
        .range(from, to)
        .then(({ data, error }) => ({ data: (data ?? []) as ComercialActivity[], error }))
    )
  },

  async getCampaignsByBroker(brokerId: string): Promise<ComercialCockpitCampaigns> {
    if (!brokerId) {
      return {
        minimetasQuantidade: 0,
        campeaoMes: [],
        decoleVendas: [],
        decoleProducao: [],
        reiImediatosQuantidade: 0,
        bateuLevouJun: null,
        bateuLevouOut: null,
        indicacoesApp: null,
        indicacoesFarma: null,
      }
    }

    const [
      minimetasRows,
      campeaoRows,
      decoleVendasRows,
      decoleProducaoRows,
      reiRows,
      bateuJunRows,
      bateuOutRows,
      indicacoes,
    ] = await Promise.all([
      fetchAllPages<{ value: number | null }>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_mm')
          .select('value')
          .eq('broker_id', brokerId)
          .range(from, to)
          .then(({ data, error }) => ({ data: (data ?? []) as { value: number | null }[], error }))
      ),
      fetchAllPages<DashCorretorCampeaoMesEntry>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_cm')
          .select(
            `
            value,
            campaign_id,
            broker_id,
            reference_month,
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (name, id_scgp, classification, state)
          `
          )
          .eq('broker_id', brokerId)
          .order('reference_month', { ascending: false })
          .range(from, to)
          .then(({ data, error }) => ({
            data: (data ?? []).map((row: Record<string, unknown>) => {
              const campaign = row.campaign as { code?: number; description?: string | null } | null
              const broker = row.broker as {
                name?: string | null
                id_scgp?: number | null
                classification?: string | null
                state?: string | null
              } | null
              return {
                value: Number(row.value ?? 0),
                campaign_id: String(row.campaign_id),
                broker_id: String(row.broker_id),
                reference_month: String(row.reference_month),
                campaign_code: campaign?.code ?? 0,
                campaign_description: campaign?.description ?? null,
                broker_name: broker?.name ?? '(Sem nome)',
                broker_code: broker?.id_scgp ?? null,
                classification: broker?.classification ?? null,
                state: broker?.state ?? null,
              } satisfies DashCorretorCampeaoMesEntry
            }),
            error,
          }))
      ),
      fetchAllPages<DashCorretorDecoleInfluenciadoresEntry>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_dv')
          .select(
            `
            id,
            campaign_id,
            broker_id,
            value,
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .eq('broker_id', brokerId)
          .order('value', { ascending: false, nullsFirst: false })
          .range(from, to)
          .then(({ data, error }) => ({
            data: (data ?? []).map((row: Record<string, unknown>) => {
              const campaign = row.campaign as { code?: number | null; description?: string | null } | null
              const broker = row.broker as {
                id_scgp?: number | null
                name?: string | null
                classification?: string | null
                state?: string | null
              } | null
              return {
                id: String(row.id),
                campaign_id: String(row.campaign_id),
                broker_id: String(row.broker_id),
                value: Number(row.value ?? 0),
                broker_code: broker?.id_scgp ?? null,
                broker_name: broker?.name ?? '(Sem nome)',
                classification: broker?.classification ?? null,
                state: broker?.state ?? null,
                campaign_code: campaign?.code ?? null,
                campaign_description: campaign?.description ?? null,
              } satisfies DashCorretorDecoleInfluenciadoresEntry
            }),
            error,
          }))
      ),
      fetchAllPages<DashCorretorDecoleInfluenciadoresEntry>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_dp')
          .select(
            `
            id,
            campaign_id,
            broker_id,
            value,
            campaign:dashcorretor_campaigns_2026 (code, description),
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .eq('broker_id', brokerId)
          .order('value', { ascending: false, nullsFirst: false })
          .range(from, to)
          .then(({ data, error }) => ({
            data: (data ?? []).map((row: Record<string, unknown>) => {
              const campaign = row.campaign as { code?: number | null; description?: string | null } | null
              const broker = row.broker as {
                id_scgp?: number | null
                name?: string | null
                classification?: string | null
                state?: string | null
              } | null
              return {
                id: String(row.id),
                campaign_id: String(row.campaign_id),
                broker_id: String(row.broker_id),
                value: Number(row.value ?? 0),
                broker_code: broker?.id_scgp ?? null,
                broker_name: broker?.name ?? '(Sem nome)',
                classification: broker?.classification ?? null,
                state: broker?.state ?? null,
                campaign_code: campaign?.code ?? null,
                campaign_description: campaign?.description ?? null,
              } satisfies DashCorretorDecoleInfluenciadoresEntry
            }),
            error,
          }))
      ),
      fetchAllPages<{ value: number | null }>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_bli')
          .select('value')
          .eq('broker_id', brokerId)
          .range(from, to)
          .then(({ data, error }) => ({ data: (data ?? []) as { value: number | null }[], error }))
      ),
      fetchAllPages<Record<string, unknown>>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_bl')
          .select(
            `
            broker_id,
            paid_premium,
            pending_premium,
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .eq('broker_id', brokerId)
          .eq('appraisal_month', 6)
          .range(from, to)
          .then(({ data, error }) => ({ data: (data ?? []) as Record<string, unknown>[], error }))
      ),
      fetchAllPages<Record<string, unknown>>((from, to) =>
        supabase
          .from('dashcorretor_campaigns_2026_bl')
          .select(
            `
            broker_id,
            paid_premium,
            pending_premium,
            broker:brokers (id_scgp, name, classification, state)
          `
          )
          .eq('broker_id', brokerId)
          .eq('appraisal_month', 10)
          .range(from, to)
          .then(({ data, error }) => ({ data: (data ?? []) as Record<string, unknown>[], error }))
      ),
      dashcorretorService.getPasiIndicationsSummaryByBrokerId(brokerId),
    ])

    const mapBateuLevou = (rows: Record<string, unknown>[]): DashCorretorBateuLevouEntry | null => {
      const row = rows[0]
      if (!row) return null
      const broker = row.broker as {
        id_scgp?: number | null
        name?: string | null
        classification?: string | null
        state?: string | null
      } | null
      return {
        broker_id: String(row.broker_id),
        broker_code: broker?.id_scgp ?? null,
        broker_name: broker?.name ?? '(Sem nome)',
        classification: broker?.classification ?? null,
        state: broker?.state ?? null,
        paid_premium: Number(row.paid_premium ?? 0),
        pending_premium: Number(row.pending_premium ?? 0),
      }
    }

    return {
      minimetasQuantidade: minimetasRows.reduce((sum, row) => sum + Number(row.value ?? 0), 0),
      campeaoMes: campeaoRows,
      decoleVendas: decoleVendasRows,
      decoleProducao: decoleProducaoRows,
      reiImediatosQuantidade: reiRows.reduce((sum, row) => sum + Number(row.value ?? 0), 0),
      bateuLevouJun: mapBateuLevou(bateuJunRows),
      bateuLevouOut: mapBateuLevou(bateuOutRows),
      indicacoesApp: indicacoes.app,
      indicacoesFarma: indicacoes.farma,
    }
  },

  async loadBrokerCockpit(brokerId: string): Promise<ComercialCockpitData> {
    const [broker, quotes, activities, campaigns] = await Promise.all([
      this.getBrokerById(brokerId),
      this.getQuotesByBroker(brokerId),
      this.getActivitiesByBroker(brokerId),
      this.getCampaignsByBroker(brokerId),
    ])

    if (!broker) {
      throw new Error('Corretor não encontrado.')
    }

    const campaignContext = await loadCampaignContext(brokerId, campaigns)

    return { broker, quotes, activities, campaigns, campaignContext }
  },
}
